//@ts-nocheck
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MDButton from "../../../../components/MDButton";
import Switch from "@mui/material/Switch";
import { post, get } from "requests";
import SaveIcon from "@mui/icons-material/Save";
import { useStoreActions, useStoreState } from "easy-peasy";
import { useMerchantContext } from "./MerchantContext";
import {
  validateTransitNumber,
  validateInstitutionNumber,
  getPaymentModeOptions,
  getBankingNumberLabel
} from "../../../../utils/States";

interface MerchantPaymentInfoProps {
  dealApplicationId: string;
}

interface ContractTerms {
  collectingModeOfPayment?: string;
  paymentAbaNumber?: string;
  paymentAccountNumber?: string;
  paymentBankName?: string;
  firstSplitPaymentAmount?: number;
  firstSplitPaymentDate?: string;
  secondSplitPaymentAmount?: number;
  secondSplitPaymentDate?: string;
  // Canadian EFT fields
  paymentTransitNumber?: string;
  paymentInstitutionNumber?: string;
  paymentAccountType?: string;
}

interface Account {
  aba: string;
  name: string;
  bank: string;
  number: string;
  accountKey: string | null;
  isEditing: boolean;
  isNew: boolean;
}

interface ApiAccount {
  abaNumber?: string;
  accountName?: string;
  bankName?: string;
  accountNumber?: string;
  accountKey?: string;
}

const MerchantPaymentInfo: React.FC<MerchantPaymentInfoProps> = ({ dealApplicationId }) => {
  const { selectedCountry } = useMerchantContext();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [modeOfPayment, setModeOfPayment] = useState("");
  const [abaNumber, setAbaNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [transitNumber, setTransitNumber] = useState("");
  const [institutionNumber, setInstitutionNumber] = useState("");
  const [accountType, setAccountType] = useState("");
  const [contractTerms, setContractTerms] = useState<ContractTerms>({});
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [firstPayment, setFirstPayment] = useState("");
  const [firstPaymentDate, setFirstPaymentDate] = useState("");
  const [secondPayment, setSecondPayment] = useState("");
  const [secondPaymentDate, setSecondPaymentDate] = useState("");
  const [cachedContractTerms, setCachedContractTerms] = useState(null);
  const getContractTerms = useStoreActions((actions: any) => actions.offerStore.getContractTerms);
  const saveContractTerms = useStoreActions((actions: any) => actions.offerStore.saveContractTerms);
  const [initialValues, setInitialValues] = useState({
    modeOfPayment: "",
    abaNumber: "",
    accountNumber: "",
    bankName: "",
    transitNumber: "",
    institutionNumber: "",
    accountType: "",
  });
  const [accounts, setAccounts] = useState<Account[]>([]);

  const theme = createTheme({
    palette: {
      primary: {
        main: "#2F929F",
      },
    },
  });

  useEffect(() => {
    if (dealApplicationId) {
      fetchContractTerms();
      fetchAndSetAccountInfo();
    }
  }, [dealApplicationId]);

  const fetchContractTerms = async () => {
    try {
      const response = await getContractTerms(dealApplicationId);
      if (response?.contractTerms) {
        setCachedContractTerms(response);

        setInitialValues({
          modeOfPayment: response.contractTerms.collectingModeOfPayment || "",
          abaNumber: response.contractTerms.paymentAbaNumber || "",
          accountNumber: response.contractTerms.paymentAccountNumber || "",
          bankName: response.contractTerms.paymentBankName || "",
          transitNumber: response.contractTerms.paymentTransitNumber || "",
          institutionNumber: response.contractTerms.paymentInstitutionNumber || "",
          accountType: response.contractTerms.paymentAccountType || "",
        });

        setContractTerms({
          collectingModeOfPayment: response?.contractTerms?.collectingModeOfPayment,
          paymentAbaNumber: response?.contractTerms?.paymentAbaNumber,
          paymentAccountNumber: response?.contractTerms.paymentAccountNumber,
          paymentBankName: response?.contractTerms?.paymentBankName,
          paymentTransitNumber: response?.contractTerms?.paymentTransitNumber,
          paymentInstitutionNumber: response?.contractTerms?.paymentInstitutionNumber,
          paymentAccountType: response?.contractTerms?.paymentAccountType,
          firstSplitPaymentAmount: response?.contractTerms.firstSplitPaymentAmount,
          firstSplitPaymentDate: response?.contractTerms?.firstSplitPaymentDate,
          secondSplitPaymentAmount: response?.contractTerms?.secondSplitPaymentAmount,
          secondSplitPaymentDate: response?.contractTerms?.secondSplitPaymentDate,
        });

        setModeOfPayment(response.contractTerms.collectingModeOfPayment || "");
        setAbaNumber(response.contractTerms.paymentAbaNumber || "");
        setAccountNumber(response.contractTerms.paymentAccountNumber || "");
        setBankName(response.contractTerms.paymentBankName || "");
        setTransitNumber(response.contractTerms.paymentTransitNumber || "");
        setInstitutionNumber(response.contractTerms.paymentInstitutionNumber || "");
        setAccountType(response.contractTerms.paymentAccountType || "");
        setFirstPayment(response.contractTerms.firstSplitPaymentAmount?.toString() || "");
        setFirstPaymentDate(response.contractTerms.firstSplitPaymentDate || "");
        setSecondPayment(response.contractTerms.secondSplitPaymentAmount?.toString() || "");
        setSecondPaymentDate(response.contractTerms.secondSplitPaymentDate || "");
        setIsSplitPayment(
          (response.contractTerms.firstSplitPaymentAmount || 0) > 0 ||
            (response.contractTerms.secondSplitPaymentAmount || 0) > 0
        );
      }
    } catch (error) {
      console.error("Error fetching contract terms:", error);
    }
  };

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const isFieldEdited = () => {
    return (
      modeOfPayment !== initialValues.modeOfPayment ||
      abaNumber !== initialValues.abaNumber ||
      accountNumber !== initialValues.accountNumber ||
      bankName !== initialValues.bankName ||
      transitNumber !== initialValues.transitNumber ||
      institutionNumber !== initialValues.institutionNumber ||
      accountType !== initialValues.accountType
    );
  };

  const savePaymentDetails = async () => {
    cachedContractTerms.applicationId = dealApplicationId;
    cachedContractTerms.contractTerms.collectingModeOfPayment = modeOfPayment;

    // Save appropriate fields based on country and payment mode
    if (selectedCountry === "CA" && modeOfPayment === "EFT") {
      cachedContractTerms.contractTerms.paymentTransitNumber = transitNumber;
      cachedContractTerms.contractTerms.paymentInstitutionNumber = institutionNumber;
      cachedContractTerms.contractTerms.paymentAccountType = accountType;
      // Clear US field
      cachedContractTerms.contractTerms.paymentAbaNumber = null;
    } else {
      cachedContractTerms.contractTerms.paymentAbaNumber = abaNumber;
      // Clear Canadian fields
      cachedContractTerms.contractTerms.paymentTransitNumber = null;
      cachedContractTerms.contractTerms.paymentInstitutionNumber = null;
      cachedContractTerms.contractTerms.paymentAccountType = null;
    }

    cachedContractTerms.contractTerms.paymentAccountNumber = accountNumber;
    cachedContractTerms.contractTerms.paymentBankName = bankName;
    cachedContractTerms.contractTerms.firstSplitPaymentAmount = parseFloat(firstPayment) || 0;
    cachedContractTerms.contractTerms.firstSplitPaymentDate = firstPaymentDate || null;
    cachedContractTerms.contractTerms.secondSplitPaymentAmount = parseFloat(secondPayment) || 0;
    cachedContractTerms.contractTerms.secondSplitPaymentDate = secondPaymentDate || null;
    await saveContractTerms(cachedContractTerms);
    await fetchContractTerms(dealApplicationId);
  };

  const fetchAndSetAccountInfo = async () => {
    setIsLoading(true);
    try {
      const response = await get(`fetchCollectionAccounts/${dealApplicationId}`);
      const fetchedAccounts: ApiAccount[] = response?.result || [];

      setAccounts(
        fetchedAccounts.map((account) => ({
          aba: account.abaNumber || "",
          name: account.accountName || "",
          bank: account.bankName || "",
          number: account.accountNumber || "",
          accountKey: account.accountKey || null,
          isEditing: false,
          isNew: false,
        }))
      );
    } catch (error) {
      console.error("Error fetching account info:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAccount = () => {
    const hasUnsavedNewAccount = accounts.some((acc) => acc.isNew && acc.isEditing);
    if (hasUnsavedNewAccount) return;
    setAccounts((prev) => [
      {
        name: "",
        bank: "",
        number: "",
        aba: "",
        accountKey: null,
        isEditing: true,
        isNew: true,
      },
      ...prev,
    ]);
  };

  const handleEditAccount = (index: number) => {
    setAccounts((prev) => prev.map((acc, i) => (i === index ? { ...acc, isEditing: true } : acc)));
  };

  const handleCancelEdit = (index: number) => async (event: React.MouseEvent) => {
    event.preventDefault();
    const account = accounts[index];
    if (account.isNew) {
      setAccounts((prev) => prev.filter((_, i) => i !== index));
    } else {
      await fetchAndSetAccountInfo();
    }
  };

  const saveAccount = async (account: Account) => {
    const payload = {
      abaNumber: account.aba,
      accountKey: account.accountKey,
      accountName: account.name,
      accountNumber: account.number,
      bankName: account.bank,
      dealApplicationId: dealApplicationId,
    };
    return await post("addOrUpdateCollectionAccount", payload);
  };

  const deleteAccount = async (accountKey: string | null) => {
    if (!accountKey) return;
    return await post("deleteCollectionAccount", {
      accountKey,
      dealApplicationId: dealApplicationId,
    });
  };

  const handleSaveAccount = (index: number) => async () => {
    try {
      const account = accounts[index];
      await saveAccount(account);
      await fetchAndSetAccountInfo();
    } catch (error) {
      console.error("Failed to save account:", error);
    }
  };

  const handleRemoveAccount = (index: number) => async () => {
    try {
      const account = accounts[index];
      await deleteAccount(account.accountKey);
      await fetchAndSetAccountInfo();
    } catch (error) {
      console.error("Failed to delete account:", error);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
        marginBottom: "12px",
        border: "1px solid #f0f0f0",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Merchant Payment Information
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isExpanded && (
            <MDButton
              variant="gradient"
              color="primary"
              onClick={handleAddAccount}
              disabled={accounts.some((acc) => acc.isNew && acc.isEditing)}
              sx={{ fontSize: "14px" }}
            >
              + Add Account
            </MDButton>
          )}
          <IconButton onClick={toggleExpand}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {isExpanded && (
        <form>
          <Box sx={{ display: "flex", mt: 1 }}>
            <Box sx={{ width: "100%", pr: 2 }}>
              <Box sx={{ display: "flex", gap: 1, mb: 2, mt: 3, paddingX: 5 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: "#555", fontSize: "14px" }}>
                    Mode of Payment
                  </Typography>
                  <FormControl sx={{ width: "250px" }} size="small" variant="standard">
                    <Select
                      size="medium"
                      value={modeOfPayment}
                      onChange={(e) => setModeOfPayment(e.target.value)}
                    >
                      {getPaymentModeOptions(selectedCountry).map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Conditional banking fields based on country and mode */}
                {selectedCountry === "CA" && modeOfPayment === "EFT" ? (
                  <>
                    {/* Transit Number */}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ color: "#555", fontSize: "14px" }}>
                        Transit Number *
                      </Typography>
                      <TextField
                        sx={{ width: "250px" }}
                        size="small"
                        variant="standard"
                        value={transitNumber}
                        onChange={(e) => setTransitNumber(e.target.value)}
                        inputProps={{ maxLength: 5 }}
                        helperText="5-digit transit number"
                        error={transitNumber && !validateTransitNumber(transitNumber)}
                      />
                    </Box>

                    {/* Institution Number */}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ color: "#555", fontSize: "14px" }}>
                        Institution Number *
                      </Typography>
                      <TextField
                        sx={{ width: "250px" }}
                        size="small"
                        variant="standard"
                        value={institutionNumber}
                        onChange={(e) => setInstitutionNumber(e.target.value)}
                        inputProps={{ maxLength: 3 }}
                        helperText="3-digit institution number"
                        error={institutionNumber && !validateInstitutionNumber(institutionNumber)}
                      />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: "#555", fontSize: "14px" }}>
                      {getBankingNumberLabel(selectedCountry, modeOfPayment)} *
                    </Typography>
                    <TextField
                      sx={{ width: "250px" }}
                      size="small"
                      variant="standard"
                      value={abaNumber}
                      onChange={(e) => setAbaNumber(e.target.value)}
                    />
                  </Box>
                )}

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: "#555", fontSize: "14px" }}>
                    Account Number
                  </Typography>
                  <TextField
                    sx={{ width: "250px" }}
                    size="small"
                    variant="standard"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                  />
                </Box>

                {/* Account Type - Only for Canadian EFT */}
                {selectedCountry === "CA" && modeOfPayment === "EFT" && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: "#555", fontSize: "14px" }}>
                      Account Type *
                    </Typography>
                    <FormControl sx={{ width: "250px" }} size="small" variant="standard">
                      <Select
                        size="medium"
                        value={accountType}
                        onChange={(e) => setAccountType(e.target.value)}
                      >
                        <MenuItem value="BUSINESS">Business</MenuItem>
                        <MenuItem value="INDIVIDUAL">Individual</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                )}

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: "#555", fontSize: "14px" }}>
                    Bank Name
                  </Typography>
                  <TextField
                    sx={{ width: "250px" }}
                    size="small"
                    variant="standard"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                  />
                </Box>

                {isFieldEdited() && (
                  <MDButton
                    variant="text"
                    color="primary"
                    onClick={savePaymentDetails}
                    startIcon={<SaveIcon />}
                    sx={{ minWidth: 0, padding: "6px", fontSize: "15px" }}
                  >
                    Save
                  </MDButton>
                )}
              </Box>
            </Box>
          </Box>

          {/* Collection Accounts Section */}
          {accounts.length > 0 && (
            <Box sx={{ mt: 4, paddingX: 5 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Collection Accounts
              </Typography>
              {accounts.map((account, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    alignItems: "center",
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    borderRadius: "4px",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: "#555", fontSize: "14px" }}>
                      Account Name
                    </Typography>
                    <TextField
                      sx={{ width: "100%" }}
                      size="small"
                      variant="standard"
                      value={account.name}
                      onChange={(e) =>
                        setAccounts((prev) =>
                          prev.map((acc, i) =>
                            i === index ? { ...acc, name: e.target.value } : acc
                          )
                        )
                      }
                      disabled={!account.isEditing}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: "#555", fontSize: "14px" }}>
                      Bank Name
                    </Typography>
                    <TextField
                      sx={{ width: "100%" }}
                      size="small"
                      variant="standard"
                      value={account.bank}
                      onChange={(e) =>
                        setAccounts((prev) =>
                          prev.map((acc, i) =>
                            i === index ? { ...acc, bank: e.target.value } : acc
                          )
                        )
                      }
                      disabled={!account.isEditing}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: "#555", fontSize: "14px" }}>
                      Account Number
                    </Typography>
                    <TextField
                      sx={{ width: "100%" }}
                      size="small"
                      variant="standard"
                      value={account.number}
                      onChange={(e) =>
                        setAccounts((prev) =>
                          prev.map((acc, i) =>
                            i === index ? { ...acc, number: e.target.value } : acc
                          )
                        )
                      }
                      disabled={!account.isEditing}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: "#555", fontSize: "14px" }}>
                      ABA Number
                    </Typography>
                    <TextField
                      sx={{ width: "100%" }}
                      size="small"
                      variant="standard"
                      value={account.aba}
                      onChange={(e) =>
                        setAccounts((prev) =>
                          prev.map((acc, i) =>
                            i === index ? { ...acc, aba: e.target.value } : acc
                          )
                        )
                      }
                      disabled={!account.isEditing}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {account.isEditing ? (
                      <>
                        <IconButton
                          onClick={handleSaveAccount(index)}
                          color="primary"
                          size="small"
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          onClick={handleCancelEdit(index)}
                          color="default"
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          onClick={() => handleEditAccount(index)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={handleRemoveAccount(index)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </form>
      )}
    </Box>
  );
};

export default MerchantPaymentInfo;
